export interface InteractionPromptValidationResult {
  valid: boolean;
  invalidReferences: string[];
  warnings: string[];
}

export interface InteractionDependencyValidationResult {
  valid: boolean;
  blockingIssues: string[];
  warnings: string[];
}

const COMPONENT_REFERENCE_SUFFIX = /(kpi|chart|table|modal|form|input|btn|button|section|container|row|card|query)$/i;
const RESERVED_REFERENCE_TOKENS = new Set([
  'action',
  'actions',
  'auth',
  'button',
  'buttons',
  'componentids',
  'componentsummary',
  'create-record',
  'createrecord',
  'data',
  'editor',
  'fetch-schema-data',
  'fetchschemadata',
  'formdatamapping',
  'groupid',
  'interactions',
  'navigation',
  'onchange',
  'onclick',
  'onload',
  'onsubmit',
  'page_load',
  'pageload',
  'properties',
  'querytemplatecontext',
  'querytemplateid',
  'refreshtargets',
  'schemaid',
  'schemacontext',
  'submit-form-data',
  'submitformdata',
  'targetdatacomponentids',
  'trigger_refresh',
  'triggerrefresh',
  'upsert-record',
  'upsertrecord',
  'userid',
]);

function normalizeReferenceToken(token: string): string {
  return token.replace(/^#/, '').trim();
}

function hasStructuralMarker(token: string): boolean {
  return /[-_]/.test(token) || /[a-z][A-Z]/.test(token);
}

function looksLikeHardcodedReference(token: string): boolean {
  const normalized = normalizeReferenceToken(token);
  const lower = normalized.toLowerCase();
  if (normalized.length < 4) return false;
  if (RESERVED_REFERENCE_TOKENS.has(lower)) return false;
  if (!COMPONENT_REFERENCE_SUFFIX.test(normalized)) return false;
  return token.startsWith('#') || hasStructuralMarker(normalized);
}

function parseComponentSummaryIds(lines: string[] | undefined): Set<string> {
  const ids = new Set<string>();
  for (const line of lines || []) {
    for (const match of line.matchAll(/\b([A-Za-z][A-Za-z0-9_-]{2,})\b(?=\s*(?:\(|,|\]))/g)) {
      ids.add(match[1]);
    }
  }
  return ids;
}

function parseQueryTemplateNames(lines: string[] | undefined): Set<string> {
  const names = new Set<string>();
  for (const line of lines || []) {
    const match = line.match(/^(.+?)\(templateId:/);
    if (match?.[1]) {
      names.add(match[1].trim());
    }
  }
  return names;
}

function parseComponentTypes(lines: string[] | undefined): Record<string, string[]> {
  const byType: Record<string, string[]> = {};
  for (const line of lines || []) {
    const match = line.match(/^([^:]+):\s*\[(.*)\]$/);
    if (!match) continue;
    const type = match[1].trim();
    const ids: string[] = [];
    for (const idMatch of match[2].matchAll(/\b([A-Za-z][A-Za-z0-9_-]{2,})\b(?=\s*(?:\(|,|\]))/g)) {
      ids.push(idMatch[1]);
    }
    byType[type] = ids;
  }
  return byType;
}

function extractHardcodedCandidates(prompt: string): string[] {
  const candidates = new Set<string>();
  const addToken = (raw: string) => {
    if (!looksLikeHardcodedReference(raw)) return;
    candidates.add(normalizeReferenceToken(raw));
  };

  for (const match of prompt.matchAll(/`([^`\n]+)`/g)) {
    addToken(match[1]);
  }
  for (const match of prompt.matchAll(/#([A-Za-z][A-Za-z0-9_-]{2,})/g)) {
    addToken(`#${match[1]}`);
  }
  for (const match of prompt.matchAll(/\b[A-Za-z][A-Za-z0-9_-]{2,}\b/g)) {
    addToken(match[0]);
  }

  return [...candidates];
}

export function validateInteractionPromptReferences(
  prompt: string,
  artifacts: Record<string, Record<string, string[]>>,
): InteractionPromptValidationResult {
  const warnings: string[] = [];
  const visualArtifacts = artifacts['visual-editor'] || {};
  const dataArtifacts = artifacts['data'] || {};

  const knownReferences = new Set<string>([
    ...(visualArtifacts.componentIds || []),
    ...parseComponentSummaryIds(visualArtifacts.componentSummary),
    ...parseQueryTemplateNames(dataArtifacts.queryTemplateContext),
  ]);

  if (!prompt.includes('{{section:visual-editor:componentIds}}')) {
    warnings.push('Interaction prompt is missing {{section:visual-editor:componentIds}} enrichment token');
  }
  if (knownReferences.size === 0) {
    warnings.push('No visual-editor component IDs are available for interaction prompt validation');
  }

  const invalidReferences = extractHardcodedCandidates(prompt)
    .filter((candidate) => !knownReferences.has(candidate))
    .sort((a, b) => a.localeCompare(b));

  return {
    valid: invalidReferences.length === 0,
    invalidReferences,
    warnings,
  };
}

export function validateInteractionPhaseDependencies(
  prompt: string,
  artifacts: Record<string, Record<string, string[]>>,
): InteractionDependencyValidationResult {
  const warnings: string[] = [];
  const blockingIssues: string[] = [];
  const visualArtifacts = artifacts['visual-editor'] || {};
  const componentIds = new Set(visualArtifacts.componentIds || []);
  const byType = parseComponentTypes(visualArtifacts.componentSummary);

  const buttonCount =
    (byType['button']?.length || 0) +
    (byType['push-button']?.length || 0) +
    (byType['icon-button']?.length || 0);
  const inputCount =
    (byType['input']?.length || 0) +
    (byType['textarea']?.length || 0);
  const chartCount = Object.entries(byType)
    .filter(([type]) => type === 'chart' || type.endsWith('-chart'))
    .reduce((sum, [, ids]) => sum + ids.length, 0);
  const tableCount =
    (byType['table']?.length || 0) +
    (byType['advanced-table']?.length || 0);
  const kpiCount =
    (byType['data-text']?.length || 0) +
    (byType['data-card']?.length || 0);
  const formContainerCount = [...componentIds].filter((id) => /(form|modal)/i.test(id)).length
    + ((byType['form-container']?.length || 0));

  if (Object.keys(byType).length === 0 && componentIds.size === 0) {
    warnings.push('No visual-editor artifacts are available for interaction dependency validation');
  }

  const lowerPrompt = prompt.toLowerCase();
  const expectsSubmit = /\bsubmit\b|\bsubmit-form-data\b|\bcreate-record\b|\bupsert-record\b/.test(lowerPrompt);
  const expectsValidation = /\bvalidate\b|\brequired\b|\bmin\b|\bmax\b/.test(lowerPrompt);
  const expectsButtonActions = /##\s*button actions/i.test(prompt) || expectsSubmit;
  const expectsModalOrForm = /\bmodal\b|\bform\b/.test(lowerPrompt);
  const expectsChart = /\bchart\b|\bgraph\b/.test(lowerPrompt);
  const expectsTable = /\btable\b/.test(lowerPrompt);
  const expectsKpis = /\bkpi\b|\bmetric card\b|\bdata-text\b/.test(lowerPrompt);

  if (expectsButtonActions && buttonCount === 0) {
    blockingIssues.push('Interaction prompt expects button actions, but prior visual artifacts contain 0 button components');
  }
  if ((expectsSubmit || expectsValidation || expectsModalOrForm) && inputCount === 0) {
    blockingIssues.push('Interaction prompt expects form validation/submission, but prior visual artifacts contain 0 input components');
  }
  if (expectsModalOrForm && formContainerCount === 0) {
    blockingIssues.push('Interaction prompt expects modal/form targets, but prior visual artifacts contain 0 modal or form components');
  }
  if (expectsChart && chartCount === 0) {
    blockingIssues.push('Interaction prompt expects chart refresh or fetch targets, but prior visual artifacts contain 0 chart components');
  }
  if (expectsTable && tableCount === 0) {
    blockingIssues.push('Interaction prompt expects table refresh or fetch targets, but prior visual artifacts contain 0 table components');
  }
  if (expectsKpis && kpiCount === 0) {
    blockingIssues.push('Interaction prompt expects KPI refresh or population targets, but prior visual artifacts contain 0 KPI components');
  }

  return {
    valid: blockingIssues.length === 0,
    blockingIssues,
    warnings,
  };
}
